/**
 * Horizontal scroll rail with hover arrows.
 * Extracted from Home.tsx so every carousel on the site behaves identically.
 */
import { useRef, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

export function ScrollRow({
  children, className, step = 320,
}: { children: ReactNode; className?: string; step?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const scroll = (dir: -1 | 1) =>
    ref.current?.scrollBy({ left: dir * step, behavior: "smooth" });

  return (
    <div className="relative group/rail">
      <div
        ref={ref}
        className={cn("flex gap-3 overflow-x-auto scrollbar-none scroll-smooth pb-1", className)}
      >
        {children}
      </div>

      {[-1, 1].map((dir) => (
        <button
          key={dir}
          type="button"
          onClick={() => scroll(dir as -1 | 1)}
          aria-label={dir === -1 ? "Scroll left" : "Scroll right"}
          className={cn(
            "hidden md:grid place-items-center absolute top-1/2 -translate-y-1/2 z-10",
            "w-9 h-9 rounded-full bg-white shadow-md border border-slate-200 text-slate-700",
            "opacity-0 group-hover/rail:opacity-100 transition-opacity hover:bg-slate-50",
            dir === -1 ? "left-0 -translate-x-1/2" : "right-0 translate-x-1/2",
          )}
        >
          {dir === -1 ? <ChevronLeft size={17} /> : <ChevronRight size={17} />}
        </button>
      ))}
    </div>
  );
}
